/**
 * The site's scheme owner (FR-007, contracts/web-surface.md §4).
 *
 * Three jobs: remember what the visitor chose, work out which of the two Vault
 * schemes that means right now, and hand it to Paper. Everything visual reads
 * the result through Paper's own `useTheme`/`useAppTheme` — nothing on this
 * site branches on the scheme by hand.
 *
 * The mode is persisted under `localStorage["theme"]`, the *same* key the
 * pre-paint script in `app/views/layouts/application.html.erb` reads before
 * first paint. That sharing is the whole no-flash mechanism (SC-006): the
 * shell paints the background this provider is about to ask for, so the two
 * must never disagree about the key or the resolution rule. Change both or
 * neither — the ERB side carries the matching comment.
 *
 * `resolveScheme` itself lives in `@/lib/resolve-scheme` so its unit suite
 * never has to load Paper under jsdom (research D13); it is re-exported here
 * so §4's "exported from the provider" reading holds too.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { PaperProvider } from "react-native-paper";
import { resolveScheme } from "@/lib/resolve-scheme";
import type { ColorScheme, ThemeMode } from "@/lib/resolve-scheme";
import { vaultDark, vaultLight } from "@/theme/paper-theme";

/** Literally `"theme"` — see the note above before touching this. */
const THEME_STORAGE_KEY = "theme";

const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

interface ThemeModeContextValue {
  /** What the visitor asked for; `"system"` until they say otherwise. */
  mode: ThemeMode;
  /** What is actually being rendered — never `"system"`. */
  scheme: ColorScheme;
  /** Records a choice and persists it. Drives the T042 scheme toggle. */
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/**
 * Anything other than the two explicit modes falls back to `"system"` — an
 * absent key, a value written by an older build, a corrupted one. The pre-paint
 * script is deliberately just as forgiving, so a junk value lands both sides on
 * the OS preference rather than putting them out of step.
 *
 * Storage can also be blocked outright (private windows, cookie policies), in
 * which case the choice simply does not survive the visit; that is a far better
 * outcome than a provider that throws on mount.
 */
function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);

    return stored === "light" || stored === "dark" || stored === "system"
      ? stored
      : "system";
  } catch {
    return "system";
  }
}

function persistMode(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Blocked storage: the page still honours the choice for this visit.
  }
}

function systemPrefersDarkNow(): boolean {
  return window.matchMedia(SYSTEM_DARK_QUERY).matches;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setStoredMode] = useState<ThemeMode>(readStoredMode);

  /**
   * Following the OS live, so switching appearance while the page is open
   * repaints it without a reload (§4, edge case "Visitor switches OS
   * appearance while the page is open").
   *
   * Only while in `"system"`: an explicit choice outranks the OS, so there is
   * nothing to listen for. The query is an external store, so React reads the
   * current `matches` during the very render that switches mode — returning
   * to `"system"` lands on what is true now, in one frame, rather than
   * painting the value from when the listener was last attached and then
   * patching it from an effect.
   */
  const subscribeToSystemScheme = useCallback(
    (onChange: () => void) => {
      if (mode !== "system") {
        return () => {};
      }

      const query = window.matchMedia(SYSTEM_DARK_QUERY);
      query.addEventListener("change", onChange);

      return () => query.removeEventListener("change", onChange);
    },
    [mode],
  );
  const systemPrefersDark = useSyncExternalStore(
    subscribeToSystemScheme,
    systemPrefersDarkNow,
  );

  const scheme = resolveScheme(mode, systemPrefersDark);
  const theme = scheme === "dark" ? vaultDark : vaultLight;

  /**
   * The root element keeps painting the scheme's background after React takes
   * over, so overscroll and any area the app's own `View`s do not cover stay
   * the right colour instead of flashing back to the shell's guess; the
   * `color-scheme` hint keeps form controls, scrollbars and the caret in step.
   *
   * These are the two values the pre-paint script hardcodes (`#F9F9F9` /
   * `#181823`). It cannot import them — it runs before any bundle exists — so
   * when a background moves in `theme/vault-palette.ts`, move it there as well.
   */
  useEffect(() => {
    const root = document.documentElement;

    root.style.backgroundColor = theme.colors.background;
    root.style.colorScheme = scheme;
  }, [scheme, theme]);

  const setMode = useCallback((nextMode: ThemeMode) => {
    persistMode(nextMode);
    setStoredMode(nextMode);
  }, []);

  const value = useMemo(
    () => ({ mode, scheme, setMode }),
    [mode, scheme, setMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <PaperProvider theme={theme}>{children}</PaperProvider>
    </ThemeModeContext.Provider>
  );
}

/** The scheme choice and its setter, for the header toggle. */
export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);

  if (context === null) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }

  return context;
}

export { resolveScheme };
export type { ColorScheme, ThemeMode };
